import { z } from "zod";

/**
 * Where an MCP server's `instructions` stand with the organization's admin.
 *
 * DERIVED, never stored. `McpServer` carries no status column for the
 * instructions on purpose — the migration says so at the four CHECKs that
 * govern them: *"There is no status column for it: pending, changed and approved
 * are derived from the two hashes, exactly as a tool's status is derived from its
 * two contract hashes."* A stored column would be a second thing to keep
 * consistent with the hashes, and a drift between them would be invisible.
 *
 * The vocabulary is deliberately NOT `McpToolStatus`. It shares four spellings
 * and differs in two ways that matter: instructions have no REJECTED state (an
 * admin who does not want them simply never approves, and the text is then never
 * injected), and they have a NONE state a tool cannot have (a tool row exists
 * only because a tool was discovered, while a server that advertises no
 * instructions is the ordinary case). Reusing the enum would make both of those
 * unrepresentable and force a lie at one end or the other.
 */
export const MCP_INSTRUCTIONS_APPROVAL_STATES = [
  /** The server has never advertised instructions, and none were ever approved. */
  "NONE",
  /** Instructions were discovered and no approval is on record. NOT injected. */
  "PENDING_APPROVAL",
  /** The approved text is exactly what the server currently advertises. Injected. */
  "APPROVED",
  /** Approved once, and the server now advertises different text. NOT injected. */
  "CHANGED",
  /** Approved once, and the server no longer advertises any instructions. NOT injected. */
  "REMOVED"
] as const;

export const McpInstructionsApprovalStateSchema = z.enum(MCP_INSTRUCTIONS_APPROVAL_STATES);

export type McpInstructionsApprovalState = z.infer<typeof McpInstructionsApprovalStateSchema>;

/** The two hash columns this state is derived from, and nothing else. */
export interface McpInstructionsHashes {
  readonly discoveredInstructionsHash: string | null;
  readonly approvedInstructionsHash: string | null;
}

/**
 * The one derivation of the state from the two hashes.
 *
 * Compares HASHES rather than the texts themselves: both columns are governed by
 * `CHECK (… IS NULL) = (… IS NULL)` against their text, so a hash is present
 * exactly when its text is, and the hash is what `computeMcpServerInstructionsHash`
 * pinned. Comparing the texts would be a second definition of "the same
 * instructions" that could disagree with the one the tool contracts use.
 */
export function deriveMcpInstructionsApprovalState(
  hashes: McpInstructionsHashes
): McpInstructionsApprovalState {
  const { discoveredInstructionsHash: discovered, approvedInstructionsHash: approved } = hashes;

  if (discovered === null) return approved === null ? "NONE" : "REMOVED";
  if (approved === null) return "PENDING_APPROVAL";
  return discovered === approved ? "APPROVED" : "CHANGED";
}

/**
 * The instructions a model may actually be given, or `null`.
 *
 * The ONE place that decision is made. Instructions reach a model's context —
 * the same trust boundary a tool description crosses — and every string in them
 * is server-authored, so text that was never approved, has changed since, or has
 * been withdrawn by the server must never be injected.
 *
 * Note which column it returns: `approvedInstructions`, never
 * `discoveredInstructions`. Under APPROVED the two are byte-identical (their
 * hashes are equal), so returning the discovered text would read as correct and
 * would silently become the wrong answer the moment the equality check was
 * loosened.
 */
export function mcpInstructionsForModel(
  server: McpInstructionsHashes & { readonly approvedInstructions: string | null }
): string | null {
  return deriveMcpInstructionsApprovalState(server) === "APPROVED"
    ? server.approvedInstructions
    : null;
}
